import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

/**
 * Helper: flatten a WPGraphQL taxonomy connection to a comma-separated string.
 * Taxonomy pick fields come back as { nodes: [{ name }] }.
 * @param connection
 */
const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

/**
 * Helper: flatten a WPGraphQL post-type relationship connection to a comma-separated string.
 * Multi pick fields come back as { nodes: [{ title }] }.
 * @param connection
 */
const postTitles = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.title ).join( ', ' ) ?? '';

/**
 * filterFn for taxonomy columns.
 * @param row
 * @param columnId
 * @param filterValue
 */
function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

/**
 * filterFn for post-type relationship columns (multi).
 * @param row
 * @param columnId
 * @param filterValue
 */
function postRefFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.title === filterValue ) ?? false;
}
postRefFilterFn.autoRemove = ( val ) => ! val;

/**
 * filterFn for scalar (custom-simple) columns.
 * @param row
 * @param columnId
 * @param filterValue
 */
function scalarFilterFn( row, columnId, filterValue ) {
	return row.original[ columnId ] === filterValue;
}
scalarFilterFn.autoRemove = ( val ) => ! val;

/**
 * filterFn for minimum numeric value on a repeatable field.
 * Passes rows where at least one value in the capacity array >= filterValue.
 * @param row
 * @param columnId
 * @param filterValue
 */
function minValueFilterFn( row, columnId, filterValue ) {
	const min = Number( filterValue );
	if ( ! min || min <= 0 ) {
		return true;
	}
	const vals = row.getValue( columnId );
	if ( ! vals || vals.length === 0 ) {
		return false;
	}
	return vals.some( ( v ) => Number( v ) >= min );
}
minValueFilterFn.autoRemove = ( val ) => ! val || Number( val ) <= 0;

/**
 * Column definitions for the space CPT.
 *
 * Field names follow WPGraphQL for Pods convention: pod prefix stripped, camelCase.
 * Verify against /graphql schema explorer if any field returns undefined.
 * @param root0
 * @param root0.onDelete
 */
export function spaceColumns( { onDelete } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this space to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'title', {
			header: __( 'Space', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<a
					href={ row.original.uri }
					className="font-semibold text-blue hover:!no-underline"
				>
					{ getValue() }
				</a>
			),
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.cities ), {
			id: 'cities',
			header: __( 'Municipality', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'contactMail', {
			header: __( 'Email', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				return v ? (
					<a
						href={ `mailto:${ v }` }
						className="text-blue hover:!no-underline"
					>
						{ v }
					</a>
				) : (
					'—'
				);
			},
		} ),

		col.accessor( 'contactPhone', {
			header: __( 'Phone', 'anacrm' ),
		} ),

		col.accessor( 'contactWebsite', {
			header: __( 'Web', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				return v ? (
					<a
						href={ v }
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue hover:!no-underline truncate block max-w-[160px]"
					>
						{ v }
					</a>
				) : (
					'—'
				);
			},
		} ),

		col.accessor( 'address', {
			header: __( 'Address', 'anacrm' ),
			cell: ( { getValue } ) => getValue() || '—',
		} ),

		col.accessor( ( row ) => taxNames( row.spaceTypes ), {
			id: 'spaceTypes',
			header: __( 'Space type', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'governance', {
			header: __( 'Governance', 'anacrm' ),
			filterFn: scalarFilterFn,
		} ),

		col.accessor(
			( row ) => {
				// capacityList is a custom GraphQL field returning all room capacities as [Float].
				const val = row.capacityList;
				if ( ! val || val.length === 0 ) {
					return [];
				}
				return val.map( Number ).filter( ( n ) => ! isNaN( n ) );
			},
			{
				id: 'capacity',
				header: __( 'Capacity', 'anacrm' ),
				cell: ( { getValue } ) => {
					const vals = getValue();
					return vals && vals.length ? vals.join( ' / ' ) : '—';
				},
				filterFn: minValueFilterFn,
			}
		),

		col.accessor( ( row ) => postTitles( row.service ), {
			id: 'service',
			header: __( 'Services', 'anacrm' ),
			filterFn: postRefFilterFn,
		} ),
	];
}
